/* External */
import {
  memo, useEffect, useCallback, useState, useRef,
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import CardProfile from '@dtvgo/card-profile';
import Icon from '@dtvgo/icon';

/* Components */
import FocusableButtonprofile from '../../../../components/FocusableButtonProfile';

/* Storage */
import { deleteProfile } from '../../../../storage/profiles/actions';

/* Utils */
import { deleteProfileAPI } from '../../../../services/profiles';
import {
  BR_GET_LAST_RATING,
  colors,
  TYPE_ACTIONS_PROFILE,
} from '../../../../utils/constants';
import { Routes, getLink } from '../../../../utils/router';
import { useCountry } from '../../../../providers/ConfigProvider';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';
import { useOnBackPress } from '../../../../utils/keyDownEvents';
import { featureFlagParentalControl, getAgeRating } from '../../../../utils/profiles';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../utils/googleTags';

/* Style */
import './ConfirmDeleteProfile.scss';

function ConfirmDeleteProfile({ realFocusKey, setFocus }) {
  const history = useHistory();
  const params = useParams();
  const dispatch = useDispatch();
  const location = useLocation();
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const [profiles] = useState(userProfiles);
  const selectedProfile = profiles?.find((profile) => profile?.id === params.id);
  const confirmDeleteProfileFocusKey = `${realFocusKey}-first-focus-confirm-delete-profile`;
  const country = useCountry();
  const { commonTaggingEvent } = useGtmLocalContext();
  const tagsMountRef = useRef(true);
  const hasParentalControl = featureFlagParentalControl(country);

  useEffect(() => {
    setFocus(confirmDeleteProfileFocusKey);
  }, [confirmDeleteProfileFocusKey, setFocus, selectedProfile]);

  const profileCommonTagEvent = useCallback(({ eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_DELETE,
        eventName,
      });
    }
  }, [commonTaggingEvent]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = false;
    }
  }, [profileCommonTagEvent, country]);

  const handleClick = useCallback(() => {
    const editedData = {
      profile_id: selectedProfile?.id,
      profile_custom_age: selectedProfile?.ageRating || getAgeRating(BR_GET_LAST_RATING),
      profile_unrated_content: selectedProfile?.isNotRatedContentAllowed,
    };
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_DELETE.CONFIRM_DELETE_PROFILE });
    deleteProfileAPI(params.id).then((res) => {
      dispatch(deleteProfile(res.data.profiles));
      localStorage.setItem('profiles', JSON.stringify(res.data.profiles));
      history.push(getLink(Routes.Private.Profiles), {
        delete: true,
        profileEditedData: editedData,
      });

      return true;
    }).catch((error) => {
      console.error(error);
      history.push(getLink(Routes.Private.Profiles.Error), {
        source: 'MIDDLEWARE',
        location: 'PROFILE_DELETE',
        type: 'UNEXPECTED',
        externCode: error?.response?.status,
        typeError: TYPE_ACTIONS_PROFILE.DELETE,
        parentalControl: hasParentalControl,
        profileEditedData: editedData,
      });
      return false;
    });
  }, [dispatch, history, profileCommonTagEvent, hasParentalControl, selectedProfile, params]);

  const notNow = useCallback(() => {
    profileCommonTagEvent({ eventName: GTM_EVENT_NAMES.PROFILES_DELETE.NOTNOW_DELETE_PROFILE });
    history.push(getLink(Routes.Private.Profiles));
  }, [history, profileCommonTagEvent]);

  useOnBackPress({
    listener: () => {
      profileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PROFILES_DELETE.BACK_PROFILES,
      });
      history.goBack();
    },
  });

  return (
    <div className="dtv-html5-c-confirm-delete-profile">
      <div className="dtv-html5-c-confirm-delete-profile__icon-back">
        <Icon className="dtv-html5-c-confirm-delete-profile__icon-back-logo" name="arrow-back" />
        <FormattedMessage id="profile.delete" />
      </div>
      <div className="dtv-html5-c-confirm-delete-profile__card-profile">
        <CardProfile
          key={selectedProfile?.id}
          name={selectedProfile?.name}
          color={colors[location.state.indexColor]}
        />
      </div>
      <div className="dtv-html5-c-confirm-delete-profile__title">
        <FormattedMessage id="profile.delete.confirm" />
      </div>
      <div className="dtv-html5-c-confirm-delete-profile__subtitle">
        <FormattedMessage id="profile.delete.favorites.list" />
      </div>
      <div className="dtv-html5-c-confirm-delete-profile__buttons">
        <FocusableButtonprofile
          onEnterPress={handleClick}
          className="dtv-btn-blue dtv-html5-c-confirm-delete-profile__button"
          type="submit"
          secondGray
          bordered
          focusKey={confirmDeleteProfileFocusKey}
        >
          <FormattedMessage id="profile.delete" />
        </FocusableButtonprofile>
        <FocusableButtonprofile
          onEnterPress={notNow}
          className="dtv-btn-tv dtv-html5-c-confirm-delete-profile__button"
          type="submit"
          secondGray
          bordered
        >
          <FormattedMessage id="common.not.now" />
        </FocusableButtonprofile>

      </div>
    </div>
  );
}

ConfirmDeleteProfile.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(ConfirmDeleteProfile)));
